import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { mergeMap, map, catchError } from "rxjs/operators";
import { ApiDataService } from "src/app/service/api-data.service";
import * as AutoArchiveActions from "./auto-archive.actions";
import * as ErrorNotificationActions from "../notification/notification.actions";

@Injectable()
export class AutoArchiveEffects {
    private actions$ = inject(Actions);
    private apiDataService = inject(ApiDataService);

    loadRules$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AutoArchiveActions.loadRules),
            mergeMap(() =>
                this.apiDataService.getAutoArchiveRules().pipe(
                    map((rules) =>
                        AutoArchiveActions.loadRulesSuccess({ rules })
                    ),
                    catchError((error) =>
                        of(
                            AutoArchiveActions.loadRulesFailure({
                                error: error.message,
                            }),
                            ErrorNotificationActions.errorNotification({
                                errorMessage: `Failed to load rules: ${error.message}`,
                            })
                        )
                    )
                )
            )
        )
    );

    createRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AutoArchiveActions.createRule),
            mergeMap((action) =>
                this.apiDataService.createAutoArchiveRule(action.rule).pipe(
                    map((rule) =>
                        AutoArchiveActions.createRuleSuccess({ rule })
                    ),
                    catchError((error) =>
                        of(
                            AutoArchiveActions.createRuleFailure({
                                error: error.message,
                            }),
                            ErrorNotificationActions.errorNotification({
                                errorMessage: `Failed to create rule: ${error.message}`,
                            })
                        )
                    )
                )
            )
        )
    );

    reloadAfterCreateSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AutoArchiveActions.createRuleSuccess),
            map(() => AutoArchiveActions.loadRules())
        )
    );

    updateRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AutoArchiveActions.updateRule),
            mergeMap((action) =>
                this.apiDataService
                    .updateAutoArchiveRule(action.id, action.rule)
                    .pipe(
                        map((rule) =>
                            AutoArchiveActions.updateRuleSuccess({ rule })
                        ),
                        catchError((error) =>
                            of(
                                AutoArchiveActions.updateRuleFailure({
                                    error: error.message,
                                }),
                                ErrorNotificationActions.errorNotification({
                                    errorMessage: `Failed to update rule: ${error.message}`,
                                })
                            )
                        )
                    )
            )
        )
    );

    reloadAfterUpdateSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AutoArchiveActions.updateRuleSuccess),
            map(() => AutoArchiveActions.loadRules())
        )
    );

    deleteRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AutoArchiveActions.deleteRule),
            mergeMap((action) =>
                this.apiDataService.deleteAutoArchiveRule(action.id).pipe(
                    map(() =>
                        AutoArchiveActions.deleteRuleSuccess({ id: action.id })
                    ),
                    catchError((error) =>
                        of(
                            AutoArchiveActions.deleteRuleFailure({
                                error: error.message,
                            }),
                            ErrorNotificationActions.errorNotification({
                                errorMessage: `Failed to delete rule: ${error.message}`,
                            })
                        )
                    )
                )
            )
        )
    );

    reloadAfterDeleteSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AutoArchiveActions.deleteRuleSuccess),
            map(() => AutoArchiveActions.loadRules())
        )
    );
}
